import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { BadgeCheck } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";
import { api, fmt } from "../client";
import { Panel, Empty, Field } from "../ui";

const FIELDS = [["name", "Venue name"], ["category", "Category"], ["address", "Address"], ["city", "City"], ["zip", "Zip"], ["contact_email", "Contact email"]];

const Review = ({ app, onClose, onDone }) => {
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => { if (app) setF(Object.fromEntries(FIELDS.map(([k]) => [k, app[k] || ""]))); }, [app]);
  const approve = async (e) => {
    e.preventDefault(); setBusy(true);
    try {
      const r = await api(`/staff/partner-applications/${app.id}/approve`, { method: "POST", body: { ...f, contact_email: f.contact_email || null } });
      toast.success(r.geocode_source === "nominatim" ? "Published to Sparkd" : "Published (used city center location)");
      onDone();
    } catch (e2) { toast.error(e2.message); } finally { setBusy(false); }
  };
  return (
    <Dialog open={!!app} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="staff-root max-h-[90vh] overflow-y-auto border-[var(--s-line)] bg-[var(--s-panel)]" data-testid="partner-app-modal">
        <DialogHeader><DialogTitle className="s-display">Review application {app?.ref}</DialogTitle></DialogHeader>
        {app?.message && <p className="rounded-lg bg-white/[0.03] p-3 text-sm text-[var(--s-dim)]">{app.message}</p>}
        <form onSubmit={approve} className="space-y-4">
          {FIELDS.map(([k, l]) => (
            <Field key={k} label={l}><Input required={k !== "zip" && k !== "contact_email"} type={k === "contact_email" ? "email" : "text"} value={f[k] || ""} onChange={(e) => setF({ ...f, [k]: e.target.value })} className="s-input" data-testid={`partner-app-${k}`} /></Field>
          ))}
          <Button type="submit" disabled={busy} className="s-btn w-full" data-testid="partner-app-approve"><BadgeCheck size={16} className="mr-2" />{busy ? "Publishing…" : "Approve & Publish to Sparkd"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default function PartnerApplications({ onCount, onPublished }) {
  const [rows, setRows] = useState(null);
  const [sel, setSel] = useState(null);
  const load = useCallback(() => {
    api("/staff/partner-applications").then((r) => { setRows(r); onCount(r.length); }).catch((e) => toast.error(e.message));
  }, [onCount]);
  useEffect(() => { load(); }, [load]);
  return (
    <>
      {rows === null ? <p className="text-sm text-[var(--s-dim)]">Loading…</p> : rows.length === 0 ? <Empty data-testid="partner-apps-empty">No pending partner applications.</Empty> : (
        <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3" data-testid="partner-apps-list">
          {rows.map((a) => (
            <Panel key={a.id} className="s-rise space-y-2 p-5" data-testid={`partner-app-card-${a.id}`}>
              <p className="s-display text-lg">{a.name}</p>
              <p className="text-sm text-[var(--s-dim)]">{[a.category, a.address, a.city].filter(Boolean).join(" · ") || "—"}</p>
              <p className="break-all text-sm">{a.contact_email || "—"}</p>
              <p className="s-mono text-[10px] text-[var(--s-dim)]">{a.ref} · {fmt(a.created_at)}</p>
              <Button size="sm" className="s-btn" onClick={() => setSel(a)} data-testid={`partner-app-review-${a.id}`}>Review application</Button>
            </Panel>
          ))}
        </div>
      )}
      <Review app={sel} onClose={() => setSel(null)} onDone={() => { setSel(null); load(); onPublished(); }} />
    </>
  );
}
